/** Stage E3.1 — Technicals panel (TanStack Query — MEDIUM stale) */
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { keys } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { formatLocalDateISO } from "@/lib/dates";

function fmt(n, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

function RsiTone(v) {
  if (v == null) return "";
  if (v >= 70) return "pnl-down";
  if (v <= 30) return "pnl-up";
  return "";
}

function TrendChip({ trend }) {
  if (!trend) return null;
  const cls = trend.includes("UP") ? "pnl-up" : trend.includes("DOWN") ? "pnl-down" : "text-signal-yellow";
  return <span className={`chip !text-[10px] border-current ${cls}`}>{trend.replace("_", " ")}</span>;
}

function Row({ label, value, tone = "" }) {
  return (
    <div className="grid grid-cols-2 gap-2 py-1 text-xs border-b border-border/30">
      <span className="text-muted-foreground uppercase tracking-wide text-[10px]">{label}</span>
      <span className={`mono text-right ${tone}`}>{value}</span>
    </div>
  );
}

export default function TechnicalsPanel({ symbol }) {
  const { data: t, isLoading: loading, isError, refetch } = useQuery({
    queryKey: keys.technicals(symbol),
    queryFn: async () => (await api.get(`/market-intel/technicals/${symbol}`)).data,
    enabled: !!symbol,
    staleTime: STALE.MEDIUM,
    gcTime: GC.MEDIUM,
  });

  if (loading) return <div className="h-32 flex items-center text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin mr-2"/>Computing…</div>;
  if (isError) return (
    <div className="border border-dashed border-signal-red/40 p-4 text-center text-xs text-muted-foreground" role="alert">
      Failed to load technicals. <button onClick={() => refetch()} className="underline underline-offset-2 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" data-testid="technicals-retry">Retry</button>
    </div>
  );
  if (!t?.available) return <div className="border border-dashed border-border/40 p-4 text-center text-xs text-muted-foreground">Technical data unavailable for {symbol}.</div>;

  const ma = t.moving_averages || {};
  const boll = t.bollinger || {};
  const macd = t.macd || {};
  const week = t.week52 || {};
  const sr = t.support_resistance || {};

  return (
    <div className="space-y-3" data-testid="technicals-panel">
      <div className="flex items-center gap-2">
        <div className="label-cap">Technicals · {symbol} · {t.timeframe}</div>
        <TrendChip trend={t.trend}/>
        <span className="ml-auto mono text-[10px] text-muted-foreground" title={t.as_of}>as of {formatLocalDateISO(t.as_of)}</span>
      </div>
      <Row label="Price" value={`$${fmt(t.price)}`}/>
      <Row label="RSI(14)" value={fmt(t.rsi14, 1)} tone={RsiTone(t.rsi14)}/>
      <Row label="MACD / Signal" value={`${fmt(macd.macd, 3)} / ${fmt(macd.signal, 3)}`} tone={macd.histogram >= 0 ? "pnl-up" : "pnl-down"}/>
      <Row label="MACD histogram" value={fmt(macd.histogram, 3)} tone={macd.histogram >= 0 ? "pnl-up" : "pnl-down"}/>
      <Row label="MA 20/50/200" value={`${fmt(ma.ma20)} / ${fmt(ma.ma50)} / ${fmt(ma.ma200)}`}/>
      <Row label="Bollinger Upper/Mid/Low" value={`${fmt(boll.upper)} / ${fmt(boll.middle)} / ${fmt(boll.lower)}`}/>
      <Row label="Bollinger width" value={`${fmt(boll.width_pct, 2)}%`}/>
      <Row label="ATR(14)" value={fmt(t.atr14, 3)}/>
      <Row label="Relative volume" value={`${fmt(t.relative_volume, 2)}x`} tone={t.relative_volume > 1.5 ? "pnl-up" : ""}/>
      <Row label="52-week high" value={`$${fmt(week.high)} (${fmt(week.distance_high_pct, 1)}%)`}/>
      <Row label="52-week low" value={`$${fmt(week.low)} (${fmt(week.distance_low_pct, 1)}%)`}/>
      <Row label="Support / Resistance" value={`$${fmt(sr.support)} — $${fmt(sr.resistance)}`}/>
    </div>
  );
}
